import i18next from "i18next";

import { Models } from "@/api/types";
import { Dictionary } from "@/core/i18n";
import { formatCurrency } from "toolbox/Money";
import pluralize from "utils/pluralize";

export enum BillingPeriod {
  Daily = 0,
  Weekly = 1,
  Monthly = 2,
  Yearly = 3,
}

export function parseBillingPeriodName(value: number): keyof typeof BillingPeriod {
  if (value in BillingPeriod) {
    return BillingPeriod[value as BillingPeriod] as keyof typeof BillingPeriod;
  }
  return "Monthly";
}

export const getBillingIntervalBase = (billingPeriodInterval: string | null): string => {
  if (!billingPeriodInterval) return "month";

  const base = billingPeriodInterval.replace(/ly$/, "").toLowerCase();
  return base === "dai" ? "day" : base;
};

// Maps a membership's billing period onto the Apple Pay recurringPaymentRequest interval fields.
// Apple Pay only supports day/month/year interval units (there is no "week"), so a weekly billing
// period is expressed as a multiple of days.
export const getApplePayRecurringInterval = (
  billingPeriod: BillingPeriod,
  billingPeriodCount: number,
): { unit: "day" | "month" | "year"; count: number } => {
  const count = billingPeriodCount > 0 ? billingPeriodCount : 1;

  switch (billingPeriod) {
    case BillingPeriod.Daily:
      return { unit: "day", count };
    case BillingPeriod.Weekly:
      return { unit: "day", count: count * 7 };
    case BillingPeriod.Yearly:
      return { unit: "year", count };
    case BillingPeriod.Monthly:
    default:
      return { unit: "month", count };
  }
};

const capitalizeFirstLetter = (value: string): string =>
  value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// Deliberately not using i18next's automatic `_one`/`_other` CLDR selection here:
// for locales whose plural rules only have an "other" category (ja-JP, ko-KR,
// vi-VN, zh-Hans/Hant), `count === 1` still resolves to "other", which would make
// it impossible to omit the count for the common "$100/month" single-unit case.
// Explicit singular/plural keys give every locale the same manual control.
const intervalTranslationKeys: Record<
  string,
  { singular: keyof Dictionary; plural: keyof Dictionary }
> = {
  day: {
    singular: "billingPeriodHelpers.intervalDaySingular",
    plural: "billingPeriodHelpers.intervalDayPlural",
  },
  week: {
    singular: "billingPeriodHelpers.intervalWeekSingular",
    plural: "billingPeriodHelpers.intervalWeekPlural",
  },
  month: {
    singular: "billingPeriodHelpers.intervalMonthSingular",
    plural: "billingPeriodHelpers.intervalMonthPlural",
  },
  year: {
    singular: "billingPeriodHelpers.intervalYearSingular",
    plural: "billingPeriodHelpers.intervalYearPlural",
  },
};

export const formatBillingInterval = (
  base: string,
  billingPeriodIntervalCount: number | null,
): string => {
  const count =
    billingPeriodIntervalCount && billingPeriodIntervalCount > 0 ? billingPeriodIntervalCount : 1;
  const keys = intervalTranslationKeys[base.toLowerCase()] ?? intervalTranslationKeys.month;

  return count === 1 ? i18next.t(keys.singular) : i18next.t(keys.plural, { count });
};

export const formatBillingPeriodInterval = (
  price: number,
  billingPeriodInterval: string | null,
  billingPeriodIntervalCount: number | null = 1,
) => {
  const base = getBillingIntervalBase(billingPeriodInterval);
  const formattedInterval = formatBillingInterval(base, billingPeriodIntervalCount);

  return `${formatCurrency(price)}/${formattedInterval}`;
};

export const getBillingPeriodIntervalTreatmentSelection = (
  membershipBillingPeriodIntervalCount: number,
  membershipBillingPeriodInterval: string,
  isFlexibleMembershipsEnabled: boolean,
  periodCount: number,
  billingPeriodInterval: number | null = null,
): string => {
  if (!isFlexibleMembershipsEnabled && (!billingPeriodInterval || billingPeriodInterval <= 0)) {
    return "";
  }

  if (isFlexibleMembershipsEnabled) {
    const base = getBillingIntervalBase(membershipBillingPeriodInterval);
    return capitalizeFirstLetter(formatBillingInterval(base, periodCount));
  }

  switch (billingPeriodInterval) {
    case 12:
      return i18next.t("billingPeriodHelpers.treatmentSelectionYear");
    case 1:
      return i18next.t("billingPeriodHelpers.treatmentSelectionMonth");
    default:
      return i18next.t("billingPeriodHelpers.treatmentSelectionMonths", {
        count: billingPeriodInterval,
      });
  }
};

const beautyBankTotalInterval = (
  membershipBillingPeriodIntervalCount: number,
  beautyBankBillingPeriodInterval: number,
): number => beautyBankBillingPeriodInterval * membershipBillingPeriodIntervalCount;

export const beautyBankHeaderText = (
  isFlexibleMembershipsEnabled: boolean,
  baseBillingPeriodInterval: string,
  membershipBillingPeriodIntervalCount: number,
  beautyBankBillingPeriodInterval: number = 1,
): string => {
  if (isFlexibleMembershipsEnabled) {
    const total = beautyBankTotalInterval(
      membershipBillingPeriodIntervalCount,
      beautyBankBillingPeriodInterval,
    );
    return i18next.t("billingPeriodHelpers.bankEveryInterval", {
      interval: formatBillingInterval(baseBillingPeriodInterval, total),
    });
  }
  return i18next.t("billingPeriodHelpers.bankEveryMonth");
};

export const beautyBankText = (
  isFlexibleMembershipsEnabled: boolean,
  beautyBankCreditAmount: string,
  baseBillingPeriodInterval: string,
  membershipListing: { billingPeriodIntervalCount?: number },
  beautyBankBillingPeriodInterval: number = 1,
) => {
  if (isFlexibleMembershipsEnabled) {
    const total = beautyBankTotalInterval(
      membershipListing.billingPeriodIntervalCount ?? 1,
      beautyBankBillingPeriodInterval,
    );
    return i18next.t("billingPeriodHelpers.bankCreditEveryInterval", {
      creditAmount: beautyBankCreditAmount,
      interval: formatBillingInterval(baseBillingPeriodInterval, total),
    });
  }
  return i18next.t("billingPeriodHelpers.bankCreditMonthly", {
    creditAmount: beautyBankCreditAmount,
  });
};

export type MembershipSavingsPeriod =
  | { kind: "unknown" }
  | { kind: "month" }
  | { kind: "interval"; interval: string }
  | { kind: "mixed" };

type MembershipPeriodLine = {
  shopDisplayMembershipId?: string | null;
  membershipBillingPeriodInterval?: number | null;
  membershipBillingPeriodIntervalCount?: number | null;
};

type MembershipPeriodRule = {
  membershipId?: string | null;
};

const membershipIdsOn = (rules: readonly MembershipPeriodRule[] | null | undefined): string[] =>
  (rules ?? []).flatMap((rule) => (rule.membershipId ? [rule.membershipId] : []));

export const membershipSavingsPeriod = (
  lines: readonly MembershipPeriodLine[],
  rules?: {
    inclusions?: readonly MembershipPeriodRule[] | null;
    requirements?: readonly MembershipPeriodRule[] | null;
  } | null,
): MembershipSavingsPeriod => {
  const namedIds = new Set([
    ...membershipIdsOn(rules?.inclusions),
    ...membershipIdsOn(rules?.requirements),
  ]);
  const membershipLines = lines.filter((line) => line.shopDisplayMembershipId);
  const targeted =
    namedIds.size > 0
      ? membershipLines.filter(
          (line) =>
            line.shopDisplayMembershipId != null && namedIds.has(line.shopDisplayMembershipId),
        )
      : membershipLines;

  const labels = targeted.map((line) => {
    const count = line.membershipBillingPeriodIntervalCount;
    if (line.membershipBillingPeriodInterval == null || count == null || count < 1) {
      return null;
    }
    const parsed = parseBillingPeriodName(line.membershipBillingPeriodInterval);
    return formatBillingInterval(getBillingIntervalBase(parsed), count);
  });

  const known = labels.filter((label): label is string => label != null);
  if (known.length === 0) {
    return { kind: "unknown" };
  }
  if (known.length !== labels.length || new Set(known).size > 1) {
    return { kind: "mixed" };
  }

  const interval = known[0];
  if (interval === i18next.t(intervalTranslationKeys.month.singular)) {
    return { kind: "month" };
  }
  return { kind: "interval", interval };
};

export const repeatCashEntryText = (
  relatedMembership: Models<"MembershipModel"> | null | undefined,
) => {
  const parsedBillingPeriodInterval = parseBillingPeriodName(
    relatedMembership?.billingPeriodInterval ?? 1,
  );
  const visibleMembershipBillingPeriodInterval = getBillingIntervalBase(
    parsedBillingPeriodInterval,
  );
  const formatBillingPeriodInterval = formatBillingInterval(
    visibleMembershipBillingPeriodInterval,
    relatedMembership?.billingPeriodIntervalCount ?? 1,
  );

  if ((relatedMembership?.billingPeriodIntervalCount ?? 1) > 1) {
    return i18next.t("billingPeriodHelpers.everyInterval", {
      interval: formatBillingPeriodInterval,
    });
  }
  return `${parsedBillingPeriodInterval}`;
};
